import React, { useState, useEffect, useRef } from 'react';
import {
  FaUser, 
  FaCalendarAlt, 
  FaPrescriptionBottle,
  FaFlask,
  FaXRay,
  FaDna,
  FaSyringe,
  FaStethoscope,
  FaFileMedical,
  FaCheckSquare,
  FaSquare,
  FaEuroSign,
  FaSpinner,
  FaSave,
  FaEdit,
  FaTrash
} from 'react-icons/fa';
import { 
  BsSearch, 
  BsCheckAll, 
  BsSquare,
  BsCalculator 
} from 'react-icons/bs';
import { MdSelectAll } from 'react-icons/md';
import { 
  getPatientActivities,
  ACTIVITY_TYPES,
  createBillableActivitiesMassive 
} from '../../../../BillableActivities/Services/patientActivitiesService';
import { 
  TAX_PROFILES 
} from '../../../../BillableActivities/Services/billingService';
import {API_BASE_URL, API_DOCTOR_LIST } from '../../../../../helpers/urlHelper';
import OrderSplit from './OrderSplit';

const NewOrderTreatment = ({ 
  show = false,
  handleClose = () => {},
  onSave = () => {},
  patientData = null,
  currentCenterId = null
}) => {
  // Estados principales - comenzamos directamente en step 2 (actividades)
  const [currentStep, setCurrentStep] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activitySplits, setActivitySplits] = useState(new Map());
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [activityPricing, setActivityPricing] = useState(new Map());
  const [editingPricing, setEditingPricing] = useState(null);
  
  // Estados para actividades del paciente
  const [patientActivities, setPatientActivities] = useState({});
  const [selectedActivities, setSelectedActivities] = useState(new Map());
  const [activityFilters, setActivityFilters] = useState({
    include_types: [],
    date_from: '',
    date_to: '',
    only_unbilled: true
  });
  
  // Estados para configuración
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
  
  // Estado para controlar los desplegables
  const [openAccordion, setOpenAccordion] = useState(null);
  
  // Refs
  const offcanvasRef = useRef(null);

  // Función para obtener el nombre completo del paciente
  const getPatientFullName = (patient) => {
    if (!patient) return 'Paciente Desconocido';
    const firstName = patient.patientName || patient.name || '';
    const lastName = patient.surname || '';
    return `${firstName} ${lastName}`.trim() || 'Sin nombre';
  };

  // Pricing por defecto para nuevas actividades
  const getDefaultPricing = () => ({
    unit_price: 75,
    qty: 1,
    discount: 0,
    tax_profile: TAX_PROFILES.EXENTO,
    tax_rate: 0,
    notes: ''
  });
  
  const handleActivityPricingChange = (activityKey, field, value) => {
    setActivityPricing(prev => {
      const newPricing = new Map(prev);
      const currentPricing = newPricing.get(activityKey) || getDefaultPricing();
      
      const updatedPricing = { ...currentPricing, [field]: value };
      
      if (field === 'tax_profile') {
        updatedPricing.tax_rate = value === TAX_PROFILES.IVA21 ? 21 : 0;
      }
      
      newPricing.set(activityKey, updatedPricing);
      return newPricing;
    });
  };

  const getActivityPricing = (activityKey) => {
    return activityPricing.get(activityKey) || getDefaultPricing();
  };

  const calculateActivityTotal = (pricing) => {
    const subtotal = (pricing.unit_price * pricing.qty) - pricing.discount;
    const tax = subtotal * (pricing.tax_rate / 100);
    return subtotal + tax;
  };

  // Iconos para tipos de actividades
  const getActivityIcon = (activityType) => {
    const icons = {
      [ACTIVITY_TYPES.APPOINTMENTS]: FaCalendarAlt,
      [ACTIVITY_TYPES.PRESCRIPTIONS]: FaPrescriptionBottle,
      [ACTIVITY_TYPES.LABORATORY]: FaFlask,
      [ACTIVITY_TYPES.IMAGING]: FaXRay,
      [ACTIVITY_TYPES.MOLECULAR]: FaDna,
      [ACTIVITY_TYPES.VACCINATIONS]: FaSyringe,
      [ACTIVITY_TYPES.TREATMENTS]: FaStethoscope,
      [ACTIVITY_TYPES.MEDICAL_RECORDS]: FaFileMedical
    };
    return icons[activityType] || FaFileMedical;
  };

  const fetchDoctors = async () => {
    try {
      setIsLoadingDoctors(true);
      console.log('Cargando lista de doctores...');
      
      const response = await fetch(`${API_DOCTOR_LIST}`);
      if (!response.ok) {
        throw new Error('Failed to fetch doctors');
      }
      
      const data = await response.json();
      const doctorsArray = data?.data || data || [];
      
      const validDoctors = doctorsArray.filter(user => 
        ['doctor', 'medico', 'physician'].includes(user.role?.toLowerCase())
      );
      
      console.log(`Doctores cargados: ${validDoctors.length}`);
      setDoctors(validDoctors);
      
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setDoctors([]);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Función para cargar actividades del paciente
  const loadPatientActivities = async (patientId) => {
    try {
      setIsLoading(true);
      console.log('Loading activities for patient:', patientId);
      const response = await getPatientActivities(patientId, activityFilters);
      
      if (response?.data) {
        setPatientActivities(response.data.available_activities || {});
      }
    } catch (error) {
      console.error('Error loading patient activities:', error);
      setPatientActivities({});
    } finally {
      setIsLoading(false);
    }
  };

  // Función para manejar cambios en los splits
  const handleSplitChange = (activityKey, splits, isValid) => {
    console.log('Split change:', activityKey, splits, isValid);
    setActivitySplits(prev => new Map(prev.set(activityKey, { splits, isValid })));
  };

  // Función para cargar compañías disponibles
  const fetchAvailableCompanies = async () => {
    try {
      setIsLoadingCompanies(true);
      console.log('Cargando compañías disponibles...');
      
      const url = `${API_BASE_URL}/healthCenters/clinics`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Error fetching companies');
      }
      
      const data = await response.json();
      console.log('Compañías cargadas:', data);
      
      const companiesFormatted = (data?.data || data || []).map(company => ({
        id: company._id,
        type: 'Company',
        name: company.name,
        display_name: company.name,
        cif: company.cif
      }));
      
      setAvailableCompanies(companiesFormatted);
      
    } catch (error) {
      console.error('Error loading companies:', error);
      setAvailableCompanies([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const areAllSplitsValid = () => {
    console.log('🔍 Validando splits...');
    let allValid = true;
    
    for (let [key, config] of activitySplits.entries()) {
      console.log(`Validando split ${key}:`, config);
      if (!config || !config.isValid) {
        console.log(`❌ Split inválido encontrado: ${key}`);
        allValid = false;
      }
    }
    
    console.log('Resultado validación:', allValid);
    return allValid;
  };

  const resetSplitsForActivity = (activityKey, patientId, patientName) => {
    console.log(`🔄 Inicializando split para actividad ${activityKey}`);
    
    if (!activitySplits.has(activityKey)) {
      const defaultSplit = [{
        payer_id: patientId,
        payer_type: 'User',
        percent: 100,
        max_amount: null
      }];
      
      console.log(`📝 Creando split por defecto:`, defaultSplit);
      
      setActivitySplits(prev => {
        const newMap = new Map(prev);
        newMap.set(activityKey, { 
          splits: defaultSplit, 
          isValid: true 
        });
        console.log(`💾 Split guardado en Map:`, newMap.get(activityKey));
        return newMap;
      });
    } else {
      console.log(`✅ Split ya existe para ${activityKey}:`, activitySplits.get(activityKey));
    }
  };

  const debugSplits = () => {
    console.log('🐛 === DEBUG SPLITS ===');
    console.log('Total actividades seleccionadas:', selectedActivities.size);
    console.log('Total splits configurados:', activitySplits.size);
    
    selectedActivities.forEach((activity, key) => {
      const splitConfig = activitySplits.get(key);
      console.log(`Actividad ${key}:`);
      console.log('  - Actividad:', activity.description);
      console.log('  - Split config:', splitConfig);
      console.log('  - Es válido:', splitConfig?.isValid);
      console.log('  - Splits:', splitConfig?.splits);
    });
    
    console.log('Splits válidos:', areAllSplitsValid());
  };

  // Función para seleccionar actividades individuales
  const handleActivitySelect = (activityType, activity) => {
    const key = `${activityType}_${activity.element_id}`;
    const newSelection = new Map(selectedActivities);
    
    if (newSelection.has(key)) {
      newSelection.delete(key);
    } else {
      newSelection.set(key, {
        ...activity,
        source_type: activityType,
        module_id: activity.element_id,
        pricing: getDefaultPricing()
      });
    }
    
    setSelectedActivities(newSelection);
  };

  // Función para seleccionar todas las actividades de un tipo
  const handleSelectAllByType = (activityType) => {
    const activities = getFilteredActivities(patientActivities[activityType] || []);
    const newSelection = new Map(selectedActivities);
    
    const typeActivitiesSelected = activities.every(activity => 
      newSelection.has(`${activityType}_${activity.element_id}`)
    );
    
    if (typeActivitiesSelected) {
      activities.forEach(activity => {
        newSelection.delete(`${activityType}_${activity.element_id}`);
      });
    } else {
      activities.forEach(activity => {
        const key = `${activityType}_${activity.element_id}`;
        newSelection.set(key, {
          ...activity,
          source_type: activityType,
          module_id: activity.element_id,
          pricing: getDefaultPricing()
        });
      });
    }
    
    setSelectedActivities(newSelection);
  };

  // Función para seleccionar TODAS las actividades visibles
  const handleSelectAllActivities = () => {
    const newSelection = new Map(selectedActivities);
    let hasAnyUnselected = false;
    
    Object.entries(patientActivities).forEach(([activityType, activities]) => {
      const filteredActivities = getFilteredActivities(activities);
      filteredActivities.forEach(activity => {
        const key = `${activityType}_${activity.element_id}`;
        if (!newSelection.has(key)) {
          hasAnyUnselected = true;
        }
      });
    });
    
    if (hasAnyUnselected) {
      Object.entries(patientActivities).forEach(([activityType, activities]) => {
        const filteredActivities = getFilteredActivities(activities);
        filteredActivities.forEach(activity => {
          const key = `${activityType}_${activity.element_id}`;
          newSelection.set(key, {
            ...activity,
            source_type: activityType,
            module_id: activity.element_id,
            pricing: getDefaultPricing()
          });
        });
      });
    } else {
      Object.entries(patientActivities).forEach(([activityType, activities]) => {
        const filteredActivities = getFilteredActivities(activities);
        filteredActivities.forEach(activity => {
          const key = `${activityType}_${activity.element_id}`;
          newSelection.delete(key);
        });
      });
    }
    
    setSelectedActivities(newSelection);
  };

  // Función para limpiar filtros de fecha
  const clearDateFilters = () => {
    setActivityFilters({
      ...activityFilters,
      date_from: '',
      date_to: ''
    });
  };

  // Función para manejar el toggle del acordeón
  const handleAccordionToggle = (activityType) => {
    setOpenAccordion(openAccordion === activityType ? null : activityType);
  };

  // Función para proceder al paso de pricing
  const handleProceedToPricing = () => {
    if (selectedActivities.size === 0) {
      alert('Please select at least one activity');
      return;
    }
    setCurrentStep(3);
  };

  // Función para aplicar precio bulk
  const applyBulkPricing = () => {
    const unitPrice = prompt('Precio unitario para todas las actividades:', '75');
    if (unitPrice === null) return;
    
    const qty = prompt('Cantidad para todas las actividades:', '1');
    if (qty === null) return;
    
    const discount = prompt('Descuento para todas las actividades (€):', '0');
    if (discount === null) return;
    
    const taxProfile = window.confirm('¿Aplicar IVA 21%?') ? TAX_PROFILES.IVA21 : TAX_PROFILES.EXENTO;

    const newBulkPricing = {
      unit_price: parseFloat(unitPrice) || 0,
      qty: parseInt(qty) || 1,
      discount: parseFloat(discount) || 0,
      tax_profile: taxProfile,
      tax_rate: taxProfile === TAX_PROFILES.IVA21 ? 21 : 0
    };
    
    const newPricing = new Map(activityPricing);
    selectedActivities.forEach((activity, key) => {
      newPricing.set(key, { ...newBulkPricing });
    });
    setActivityPricing(newPricing);
  };

  // Función para guardar actividades facturables
  const handleSaveBillableActivities = async () => {
    try {
      setIsSaving(true);
      
      const selectedActivitiesArray = Array.from(selectedActivities.values());
      
      if (selectedActivitiesArray.length === 0) {
        alert('Por favor, selecciona al menos una actividad.');
        return;
      }
      
      if (!patientData || !patientData.patientId) {
        alert('Error: No se encontró información del paciente.');
        return;
      }
      
      console.log('🔍 Validando splits antes del envío...');
      const invalidSplits = [];
      
      selectedActivitiesArray.forEach((activity, index) => {
        const activityKey = `${activity.source_type}_${activity.element_id}`;
        const splitConfig = activitySplits.get(activityKey);
        
        console.log(`Validando split para ${activityKey}:`, splitConfig);
        
        if (!splitConfig || !splitConfig.isValid) {
          invalidSplits.push(`Actividad ${index + 1}: ${activity.description}`);
        }
        
        if (splitConfig && splitConfig.splits) {
          const emptySplits = splitConfig.splits.filter(split => 
            !split.payer_id || split.payer_id.trim() === ''
          );
          if (emptySplits.length > 0) {
            invalidSplits.push(`Actividad ${index + 1}: Pagadores sin seleccionar`);
          }
        }
      });
      
      if (invalidSplits.length > 0) {
        alert(`Errores de validación:\n${invalidSplits.join('\n')}`);
        return;
      }
      
      const createData = {
        patient_id: patientData.patientId,
        doctor_id: selectedDoctor?._id || null,
        center_id: currentCenterId || null,
        center_name: patientData.center_name || null,
        selected_activities: selectedActivitiesArray.map(activity => {
          const activityKey = `${activity.source_type}_${activity.element_id}`;
          const splitConfig = activitySplits.get(activityKey);
          const pricing = getActivityPricing(activityKey);
          
          let finalDoctorId = selectedDoctor?._id || activity.doctor_info?._id || null;
let finalDoctorName = selectedDoctor?.name && selectedDoctor?.surname 
  ? `${selectedDoctor.name} ${selectedDoctor.surname}`
  : activity.doctor_info?.full_name || null;
          
          let finalSplit = [];
          
          if (splitConfig && splitConfig.splits && Array.isArray(splitConfig.splits) && splitConfig.splits.length > 0) {
            const totalPercent = splitConfig.splits.reduce((sum, split) => sum + (split.percent || 0), 0);
            
            console.log(`Procesando splits para ${activityKey}:`, splitConfig.splits);
            console.log(`Total porcentaje: ${totalPercent}`);
            
            if (Math.abs(totalPercent - 100) <= 0.01) {
              finalSplit = splitConfig.splits.map(split => {
                if (!split.payer_id || split.payer_id.trim() === '') {
                  throw new Error(`Split inválido: payer_id vacío para tipo ${split.payer_type}`);
                }
                
                const enrichedSplit = {
                  payer_id: split.payer_id.trim(),
                  payer_type: split.payer_type,
                  percent: Number(split.percent),
                  max_amount: split.max_amount ? Number(split.max_amount) : null
                };
                
                if (split.payer_type === 'User' && split.payer_id === patientData.patientId) {
                  enrichedSplit.payer_name = getPatientFullName(patientData);
                } else if (split.payer_type === 'Company' || split.payer_type === 'HealthCenter') {
                  const payerInfo = availableCompanies.find(company => company.id === split.payer_id);
                  if (payerInfo) {
                    enrichedSplit.payer_name = payerInfo.name || payerInfo.display_name;
                    enrichedSplit.payer_cif = payerInfo.cif;
                    
                    if (split.payer_type === 'HealthCenter') {
                      enrichedSplit.center_type = payerInfo.center_type || 'medical_center';
                      enrichedSplit.specialty = payerInfo.specialty;
                    }
                  } else {
                    console.warn(`No se encontró información para pagador ${split.payer_id}`);
                    enrichedSplit.payer_name = `Pagador ${split.payer_id}`;
                  }
                }
                
                return enrichedSplit;
              });
            } else {
              console.warn(`Porcentajes no suman 100% para ${activityKey}, usando fallback`);
              finalSplit = [{
                payer_id: patientData.patientId,
                payer_type: 'User',
                percent: 100,
                max_amount: null,
                payer_name: getPatientFullName(patientData)
              }];
            }
          } else {
            console.log(`No hay splits configurados para ${activityKey}, usando fallback`);
            finalSplit = [{
              payer_id: patientData.patientId,
              payer_type: 'User',
              percent: 100,
              max_amount: null,
              payer_name: getPatientFullName(patientData)
            }];
          }
          
          const invalidFinalSplits = finalSplit.filter(split => !split.payer_id || split.payer_id.trim() === '');
          if (invalidFinalSplits.length > 0) {
            throw new Error(`Splits finales inválidos para actividad ${activity.description}`);
          }
          
          console.log(`✅ Splits finales para ${activityKey}:`, finalSplit);
          
          return {
            element_id: activity.element_id,
            module_id: activity.module_id || activity.element_id,
            source_type: activity.source_type,
            description: activity.description,
            performed_at: activity.performed_at || new Date().toISOString(),
            doctor_id: finalDoctorId,
            doctor_name: finalDoctorName,
            pricing: pricing,
            split: finalSplit
          };
        })
      };

      console.log('📤 Datos finales a enviar:', JSON.stringify(createData, null, 2));

      const activitiesWithEmptyPayers = createData.selected_activities.filter(activity =>
        activity.split.some(split => !split.payer_id || split.payer_id.trim() === '')
      );
      
      if (activitiesWithEmptyPayers.length > 0) {
        console.error('❌ Actividades con pagadores vacíos:', activitiesWithEmptyPayers);
        alert('Error: Se detectaron pagadores sin seleccionar. Por favor revisa la configuración.');
        return;
      }

      const response = await createBillableActivitiesMassive(
        createData.patient_id,
        createData
      );
      
      console.log('📨 Respuesta del servidor:', response);
      
      if (response && response.data) {
        const summary = response.data.summary;
        if (summary) {
          const message = `Proceso completado:\n- Actividades creadas: ${summary.created}\n- Errores: ${summary.failed}`;
          
          if (summary.failed > 0 && response.data.errors && response.data.errors.length > 0) {
            const errorDetails = response.data.errors
              .slice(0, 3)
              .map(err => `  • ${err.activity_id}: ${err.error}`)
              .join('\n');
            
            alert(`${message}\n\nPrimeros errores:\n${errorDetails}`);
          } else {
            alert(message);
          }
        } else {
          alert('¡Actividades creadas exitosamente!');
        }
        
        if (onSave) onSave(response.data);
        handleClose();
        resetModal();

        // Recargar página para ver los cambios
        window.location.reload();
      } else {
        console.error('Respuesta inesperada del servidor:', response);
        alert('Error: Respuesta inesperada del servidor');
      }
      
    } catch (error) {
      console.error('❌ Error completo:', error);
      
      let errorMessage = 'Error al crear actividades: ';
      if (error.response?.data?.error?.message) {
        errorMessage += error.response.data.error.message;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Error desconocido';
      }
      
      alert(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Función para resetear el modal
  const resetModal = () => {
    setCurrentStep(2); // Volver al step 2 (actividades)
    setPatientActivities({});
    setSelectedActivities(new Map());
    setSelectedDoctor(null);
    setDoctors([]);
    setOpenAccordion(null);
    
    setActivitySplits(new Map());
    setActivityPricing(new Map());
    setEditingPricing(null);
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    resetModal();
    handleClose();
  };

  // useEffect para cargar doctores
  useEffect(() => {
    if (show && currentStep === 2 && doctors.length === 0) {
      fetchDoctors();
    }
  }, [show, currentStep]);

  // Efecto para cargar compañías cuando se llega al step 3
  useEffect(() => {
    if (currentStep === 3 && availableCompanies.length === 0) {
      fetchAvailableCompanies();
    }
  }, [currentStep]);

  // Efecto para inicializar splits por defecto
  useEffect(() => {
    if (currentStep === 3 && patientData) {
      selectedActivities.forEach((activity, key) => {
        resetSplitsForActivity(key, patientData.patientId, getPatientFullName(patientData));
      });
    }
  }, [currentStep, selectedActivities, patientData]);

  // Efecto para inicializar precios por defecto cuando se seleccionan actividades
  useEffect(() => {
    if (currentStep === 3) {
      selectedActivities.forEach((activity, key) => {
        if (!activityPricing.has(key)) {
          setActivityPricing(prev => new Map(prev.set(key, getDefaultPricing())));
        }
      });
    }
  }, [currentStep, selectedActivities]);

  // Efecto para cargar actividades del paciente cuando se abre el modal
  useEffect(() => {
    if (show && patientData?.patientId && currentStep === 2) {
      loadPatientActivities(patientData.patientId);
    }
  }, [show, patientData, currentStep]);

  // Efecto para recargar actividades cuando cambian los filtros
  useEffect(() => {
    if (patientData?.patientId && (activityFilters.date_from || activityFilters.date_to)) {
      loadPatientActivities(patientData.patientId);
    }
  }, [activityFilters.date_from, activityFilters.date_to, patientData]);

  // Función para filtrar actividades por fecha
  const getFilteredActivities = (activities) => {
    if (!activityFilters.date_from && !activityFilters.date_to) {
      return activities;
    }
    
    return activities.filter(activity => {
      const activityDate = new Date(activity.performed_at || activity.date || activity.created_at);
      let isInRange = true;
      
      if (activityFilters.date_from) {
        const fromDate = new Date(activityFilters.date_from);
        isInRange = isInRange && activityDate >= fromDate;
      }
      
      if (activityFilters.date_to) {
        const toDate = new Date(activityFilters.date_to + 'T23:59:59.999Z');
        isInRange = isInRange && activityDate <= toDate;
      }
      
      return isInRange;
    });
  };

  // Calcular totales
  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    let total = 0;

    selectedActivities.forEach((activity, activityKey) => {
      const pricing = getActivityPricing(activityKey);
      const activitySubtotal = (pricing.unit_price * pricing.qty) - pricing.discount;
      const activityTax = activitySubtotal * (pricing.tax_rate / 100);
      const activityTotal = activitySubtotal + activityTax;
      
      subtotal += activitySubtotal;
      totalTax += activityTax;
      total += activityTotal;
    });

    return { subtotal, totalTax, total };
  };

  const getActivitiesDoctorStats = () => {
    let withDoctor = 0;
    let withoutDoctor = 0;
    const doctorCounts = {};
    
    selectedActivities.forEach((activity) => {
      if (activity.doctor_info?.full_name) {
      withDoctor++;
      const doctorKey = activity.doctor_info.full_name;
      doctorCounts[doctorKey] = (doctorCounts[doctorKey] || 0) + 1;
    } else {
      withoutDoctor++;
    }
  });
    
    const doctorsList = Object.entries(doctorCounts)
      .map(([name, count]) => `${name} (${count})`)
      .join(', ');
      
    return (
      <>
        <div>
          <strong>Actividades con doctor:</strong> {withDoctor} | 
          <strong> Sin doctor:</strong> {withoutDoctor}
        </div>
        {doctorsList && (
          <div className="mt-1">
            <strong>Doctores asignados:</strong> {doctorsList}
          </div>
          )}
      </>
    );
  };

  // Renderizado condicional por steps
  if (!show) return null;

  return (
    <div 
      className={`offcanvas offcanvas-end ${show ? 'show' : ''}`}
      style={{ 
        visibility: show ? 'visible' : 'hidden',
        width: '90%',
        maxWidth: '1400px'
      }}
      tabIndex="-1"
      ref={offcanvasRef}
    >
      <div className="offcanvas-header bg-primary text-white">
        <h5 className="offcanvas-title d-flex align-items-center">
          <FaUser className="me-2" />
          Crear Actividades Facturables - {getPatientFullName(patientData)}
        </h5>
        <button
          type="button"
          className="btn-close btn-close-white"
          onClick={handleCloseModal}
          disabled={isSaving}
        ></button>
      </div>

      <div className="offcanvas-body p-0">
        {/* Progress Steps */}
        <div className="bg-light border-bottom p-3">
          <div className="row">
            <div className="col-12">
              <div className="d-flex justify-content-center">
                <div className="d-flex align-items-center">
                  <div className={`badge ${currentStep >= 2 ? 'bg-success' : 'bg-secondary'} me-2`}>2</div>
                  <span className={currentStep >= 2 ? 'text-success fw-bold' : 'text-muted'}>
                    Seleccionar Actividades
                  </span>
                  <div className="mx-3">→</div>
                  <div className={`badge ${currentStep >= 3 ? 'bg-success' : 'bg-secondary'} me-2`}>3</div>
                  <span className={currentStep >= 3 ? 'text-success fw-bold' : 'text-muted'}>
                    Configurar Facturación
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Seleccionar Actividades */}
        {currentStep === 2 && (
          <div className="p-4">
            <div className="row">
              <div className="col-12">
                <h6 className="mb-3">
                  <FaFileMedical className="me-2" />
                  Actividades del Paciente
                </h6>

                {/* Filtros */}
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="row align-items-end">
                      <div className="col-md-3">
                        <label className="form-label">Fecha Desde</label>
                        <input
                          type="date"
                          className="form-control"
                          value={activityFilters.date_from}
                          onChange={(e) => setActivityFilters({
                            ...activityFilters,
                            date_from: e.target.value
                          })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Fecha Hasta</label>
                        <input
                          type="date"
                          className="form-control"
                          value={activityFilters.date_to}
                          onChange={(e) => setActivityFilters({
                            ...activityFilters,
                            date_to: e.target.value
                          })}
                        />
                      </div>
                      <div className="col-md-2">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={clearDateFilters}
                        >
                          Limpiar Fechas
                        </button>
                      </div>
                      <div className="col-md-4 text-end">
                        <button
                          className="btn btn-primary me-2"
                          onClick={handleSelectAllActivities}
                          disabled={isLoading}
                        >
                          <MdSelectAll className="me-1" />
                          Seleccionar Todas
                        </button>
                        <span className="badge bg-info">
                          Seleccionadas: {selectedActivities.size}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Doctor Selection */}
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 className="card-title">
                      <FaStethoscope className="me-2" />
                      Asignar Doctor (Opcional)
                    </h6>
                    <div className="row">
                      <div className="col-md-6">
                        <select
                          className="form-select"
                          value={selectedDoctor?._id || ''}
                          onChange={(e) => {
                            const doctor = doctors.find(d => d._id === e.target.value);
                            setSelectedDoctor(doctor || null);
                          }}
                          disabled={isLoadingDoctors}
                        >
                          <option value="">Sin doctor asignado</option>
                          {doctors.map(doctor => (
                            <option key={doctor._id} value={doctor._id}>
                              {doctor.name} {doctor.surname} - {doctor.email}
                            </option>
                          ))}
                        </select>
                        {isLoadingDoctors && (
                          <small className="text-muted">
                            <FaSpinner className="spinner-border spinner-border-sm me-1" />
                            Cargando doctores...
                          </small>
                        )}
                      </div>
                      <div className="col-md-6">
                        <small className="text-muted">
                          El doctor se aplicará a todas las actividades seleccionadas que no tengan doctor asignado.
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activities List */}
                {isLoading ? (
                  <div className="text-center py-4">
                    <FaSpinner className="spinner-border text-primary me-2" />
                    Cargando actividades...
                  </div>
                ) : Object.keys(patientActivities).length === 0 ? (
                  <div className="alert alert-info">
                    <FaFileMedical className="me-2" />
                    No se encontraron actividades para este paciente.
                  </div>
                ) : (
                  <div className="accordion" id="activitiesAccordion">
                    {Object.entries(patientActivities).map(([activityType, activities]) => {
                      const filteredActivities = getFilteredActivities(activities);
                      
                      if (filteredActivities.length === 0) return null;

                      const IconComponent = getActivityIcon(activityType);
                      const isOpen = openAccordion === activityType;
                      const selectedInType = filteredActivities.filter(activity => 
                        selectedActivities.has(`${activityType}_${activity.element_id}`)
                      ).length;

                      return (
                        <div key={activityType} className="accordion-item">
                          <h2 className="accordion-header">
                            <button
                              className={`accordion-button ${!isOpen ? 'collapsed' : ''}`}
                              type="button"
                              onClick={() => handleAccordionToggle(activityType)}
                            >
                              <IconComponent className="me-2" />
                              {activityType.replace(/_/g, ' ').toUpperCase()}
                              <span className="badge bg-primary ms-2">
                                {selectedInType}/{filteredActivities.length}
                              </span>
                            </button>
                          </h2>
                          <div
                            className={`accordion-collapse collapse ${isOpen ? 'show' : ''}`}
                          >
                            <div className="accordion-body">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleSelectAllByType(activityType)}
                                >
                                  <BsCheckAll className="me-1" />
                                  {selectedInType === filteredActivities.length ? 'Deseleccionar' : 'Seleccionar'} Todas
                                </button>
                                <small className="text-muted">
                                  {filteredActivities.length} actividades disponibles
                                </small>
                              </div>

                              <div className="table-responsive">
                                <table className="table table-hover table-sm">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="50">
                                        <BsSquare />
                                      </th>
                                      <th>Descripción</th>
                                      <th>Fecha</th>
                                      <th>Doctor</th>
                                      <th>Estado</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredActivities.map((activity, index) => {
                                      const key = `${activityType}_${activity.element_id}`;
                                      const isSelected = selectedActivities.has(key);
                                      
                                      return (
                                        <tr
                                          key={index}
                                          className={isSelected ? 'table-primary' : ''}
                                          style={{ cursor: 'pointer' }}
                                          onClick={() => handleActivitySelect(activityType, activity)}
                                        >
                                          <td>
                                            {isSelected ? (
                                              <FaCheckSquare className="text-primary" />
                                            ) : (
                                              <FaSquare className="text-muted" />
                                            )}
                                          </td>
                                          <td>
                                            <strong>{activity.description || 'Sin descripción'}</strong>
                                            {activity.notes && (
                                              <div>
                                                <small className="text-muted">{activity.notes}</small>
                                              </div>
                                            )}
                                          </td>
                                          <td>
                                            {activity.performed_at || activity.date || activity.created_at
                                              ? new Date(activity.performed_at || activity.date || activity.created_at).toLocaleDateString()
                                              : 'Sin fecha'
                                            }
                                          </td>
                                          <td>
                                            {activity.doctor_info?.full_name || (
                                              <small className="text-muted">Sin doctor</small>
                                            )}
                                          </td>
                                          <td>
                                            <span className={`badge ${activity.is_billed ? 'bg-success' : 'bg-warning'}`}>
                                              {activity.is_billed ? 'Facturado' : 'Pendiente'}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Statistics */}
                {selectedActivities.size > 0 && (
                  <div className="card mt-3">
                    <div className="card-body">
                      <h6 className="card-title">Estadísticas de Selección</h6>
                      {getActivitiesDoctorStats()}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex justify-content-between mt-4">
                  <button
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                    disabled={isSaving}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleProceedToPricing}
                    disabled={selectedActivities.size === 0 || isSaving}
                  >
                    Continuar a Facturación ({selectedActivities.size} seleccionadas)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Configurar Facturación */}
        {currentStep === 3 && (
          <div className="p-4">
            <div className="row">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>
                    <FaEuroSign className="me-2" />
                    Configuración de Facturación
                  </h6>
                  <div>
                    <button
                      className="btn btn-outline-primary btn-sm me-2"
                      onClick={applyBulkPricing}
                    >
                      <BsCalculator className="me-1" />
                      Precio Masivo
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={debugSplits}
                    >
                      Debug Splits
                    </button>
                  </div>
                </div>

                {/* Activities Pricing */}
                <div className="row">
                  {Array.from(selectedActivities.entries()).map(([activityKey, activity]) => {
                    const pricing = getActivityPricing(activityKey);
                    const total = calculateActivityTotal(pricing);
                    const splitConfig = activitySplits.get(activityKey);

                    return (
                      <div key={activityKey} className="col-12 mb-4">
                        <div className="card">
                          <div className="card-header">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{activity.description}</strong>
                                <div>
                                  <small className="text-muted">
                                    {activity.source_type} • {activity.performed_at 
                                      ? new Date(activity.performed_at).toLocaleDateString()
                                      : 'Sin fecha'
                                    }
                                  </small>
                                </div>
                              </div>
                              <div className="text-end">
                                <div className="h6 mb-0">€{total.toFixed(2)}</div>
                                <small className={`badge ${splitConfig?.isValid ? 'bg-success' : 'bg-warning'}`}>
                                  {splitConfig?.isValid ? 'Split válido' : 'Configurar split'}
                                </small>
                              </div>
                            </div>
                          </div>
                          <div className="card-body">
                            {/* Pricing Configuration */}
                            <div className="row mb-3">
                              <div className="col-md-2">
                                <label className="form-label">Precio Unit.</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm"
                                  value={pricing.unit_price}
                                  onChange={(e) => handleActivityPricingChange(
                                    activityKey, 
                                    'unit_price', 
                                    parseFloat(e.target.value) || 0
                                  )}
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label">Cantidad</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={pricing.qty}
                                  onChange={(e) => handleActivityPricingChange(
                                    activityKey, 
                                    'qty', 
                                    parseInt(e.target.value) || 1
                                  )}
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label">Descuento</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm"
                                  value={pricing.discount}
                                  onChange={(e) => handleActivityPricingChange(
                                    activityKey, 
                                    'discount', 
                                    parseFloat(e.target.value) || 0
                                  )}
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label">IVA</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={pricing.tax_profile}
                                  onChange={(e) => handleActivityPricingChange(
                                    activityKey, 
                                    'tax_profile', 
                                    e.target.value
                                  )}
                                >
                                  <option value={TAX_PROFILES.EXENTO}>Exento</option>
                                  <option value={TAX_PROFILES.IVA21}>IVA 21%</option>
                                </select>
                              </div>
                              <div className="col-md-4">
                                <label className="form-label">Notas</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={pricing.notes}
                                  onChange={(e) => handleActivityPricingChange(
                                    activityKey, 
                                    'notes', 
                                    e.target.value
                                  )}
                                  placeholder="Notas adicionales..."
                                />
                              </div>
                            </div>

                            {/* Split Configuration */}
                            <div className="border-top pt-3">
                              <OrderSplit
  activityKey={activityKey}
  patientData={patientData}
  availableCompanies={availableCompanies}
  isLoadingCompanies={isLoadingCompanies}
  onSplitChange={handleSplitChange}
  initialSplits={splitConfig?.splits}
/>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Totals Summary */}
                {selectedActivities.size > 0 && (
                  <div className="card bg-light">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-8">
                          <h6>Resumen de Facturación</h6>
                          <p className="mb-0">
                            <strong>Actividades:</strong> {selectedActivities.size} | 
                            <strong> Splits válidos:</strong> {areAllSplitsValid() ? 'Sí' : 'No'}
                          </p>
                        </div>
                        <div className="col-md-4 text-end">
                          {(() => {
                            const totals = calculateTotals();
                            return (
                              <>
                                <div><strong>Subtotal:</strong> €{totals.subtotal.toFixed(2)}</div>
                                <div><strong>IVA:</strong> €{totals.totalTax.toFixed(2)}</div>
                                <div className="h5"><strong>Total:</strong> €{totals.total.toFixed(2)}</div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex justify-content-between mt-4">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setCurrentStep(2)}
                    disabled={isSaving}
                  >
                    ← Volver a Actividades
                  </button>
                  <button
                    className="btn btn-success btn-lg"
                    onClick={handleSaveBillableActivities}
                    disabled={selectedActivities.size === 0 || !areAllSplitsValid() || isSaving}
                  >
                    {isSaving ? (
                      <>
                        <FaSpinner className="spinner-border spinner-border-sm me-2" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Crear Actividades Facturables ({selectedActivities.size})
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewOrderTreatment;